import { Link } from "react-router-dom";
import { useConfig } from "@/contexts/ConfigContext";
import {
  Download, ArrowRight, ShieldCheck, Scale, Users, Rocket,
  TrendingUp, Lock, MapPin, Zap, Video, FileText, Check,
  Play, DollarSign, Layers, Globe, Sparkles,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════════════════
   Colega Link · Landing pública para inversionistas / experto de marketing
   URL: /para-inversionistas   (público, sin auth)
   ═══════════════════════════════════════════════════════════════════════════ */

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** GA4 event helper — envía evento cta_click con label. Silencioso si gtag no está cargado. */
function trackCTA(label) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "cta_click", {
        event_category: "investor_landing",
        event_label: label,
      });
    }
  } catch { /* noop */ }
}

export default function ParaInversionistas() {
  return (
    <div className="min-h-screen" data-testid="investor-landing">
      <Hero />
      <BigNumbers />
      <Problem />
      <Solution />
      <ModulesGrid />
      <BusinessModel />
      <Prestaciones />
      <Diferenciales />
      <Financials />
      <Assets />
      <Ask />
      <CTAFinal />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="container-app pt-16 pb-8 lg:pt-24">
      <div className="grid lg:grid-cols-5 gap-12 items-start">
        <div className="lg:col-span-3">
          <div className="pill badge-gold text-xs mb-5" data-testid="hero-eyebrow">
            <Sparkles size={12}/> INVESTOR & PARTNERSHIP BRIEF · JULIO 2026
          </div>
          <h1 className="serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
            La <span className="brand-gradient-text">infraestructura legal</span><br/>
            de Chile ya está construida.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
            Colega Link es la primera plataforma que profesionaliza el trabajo entre abogados y mediadores en Chile.
            <span className="text-foreground"> En producción, con 107 endpoints activos, Pago Protegido, verificación oficial de la Corte Suprema y un motor de precios único para mediación familiar privada.</span>
          </p>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl">
            Buscamos <strong className="text-emerald-300">partner comercial estratégico</strong> con expertise en marketing y growth para tomar la operación comercial y llevarla a su siguiente etapa.
          </p>

        <div className="mt-10 flex flex-wrap gap-3">
            <a href={`${BACKEND}/api/investor-brief.pdf`} target="_blank" rel="noopener noreferrer"
               onClick={() => trackCTA("hero_pdf")}
               className="btn-primary" data-testid="cta-download-pdf">
              <Download size={16} className="mr-1.5"/> Descargar Brief PDF
            </a>
            <a href={`${BACKEND}/api/investor-video`} target="_blank" rel="noopener noreferrer"
               onClick={() => trackCTA("hero_video")}
               className="btn-ghost" data-testid="cta-watch-video">
              <Play size={14} className="mr-1.5"/> Ver video pitch (90s)
            </a>
            <a href="https://wa.me/56941199600?text=Hola%20Cristian%2C%20vi%20el%20brief%20de%20Colega%20Link%20y%20quiero%20conversar."
               target="_blank" rel="noopener noreferrer"
               onClick={() => trackCTA("hero_whatsapp")}
               className="btn-ghost border-emerald-400/40 text-emerald-200 hover:bg-emerald-400/10" data-testid="cta-whatsapp">
              Hablemos por WhatsApp <ArrowRight size={13} className="ml-1"/>
            </a>
          </div>
        </div>

        <aside className="lg:col-span-2">
          <div className="panel p-6 space-y-4" data-testid="hero-status-card">
            <div className="label-eyebrow text-cyan-300">Estado operativo</div>
            <StatusRow ok label="Producción en vivo" value="colegalink.cl" />
            <StatusRow ok label="Endpoints API activos" value="107" />
            <StatusRow ok label="Pantallas frontend" value="31" />
            <StatusRow ok label="Testing pass ratio" value="100%" />
            <StatusRow ok label="Deuda técnica bloqueante" value="Ninguna" />
            <StatusRow warn label="Pasarela pago real" value="Pendiente Flow/Webpay" />
            <StatusRow ok label="Deploy PWA instalable" value="iOS + Android" />
          </div>
        </aside>
      </div>
    </section>
  );
}

function StatusRow({ ok, warn, label, value }) {
  const color = ok ? "text-emerald-300 bg-emerald-400/10" : warn ? "text-amber-300 bg-amber-400/10" : "text-muted-foreground bg-white/5";
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0 last:pb-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-xs px-2 py-1 rounded-md ${color} font-medium`}>{value}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
function BigNumbers() {
  const nums = [
    { n: "$130.602", l: "Tope legal mediación familiar" },
    { n: "50", l: "Cupos Fundador (cap estricto)" },
    { n: "15", l: "Estados regulados de mediación" },
    { n: "107", l: "Endpoints API operativos" },
  ];
  return (
    <section className="container-app py-10">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {nums.map((n, i) => (
          <div key={i} className="panel p-6 text-center" data-testid={`big-num-${i}`}>
            <div className="serif text-4xl brand-gradient-text">{n.n}</div>
            <div className="text-xs text-muted-foreground mt-2 uppercase tracking-wider">{n.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
function Problem() {
  const items = [
    {
      icon: <Users size={20} className="text-red-300"/>,
      title: "Delegaciones informales por WhatsApp",
      desc: "Miles de audiencias al día se coordinan por chat entre colegas. Sin trazabilidad, sin registro, sin garantía de pago.",
    },
    {
      icon: <DollarSign size={20} className="text-amber-300"/>,
      title: "Mediación familiar opaca",
      desc: "Precios pactados sin transparencia. Familias en conflicto no saben cuánto van a pagar hasta el final del proceso.",
    },
    {
      icon: <ShieldCheck size={20} className="text-cyan-300"/>,
      title: "Verificación de credenciales artesanal",
      desc: "No existe un canal centralizado para validar Corte Suprema y Registro de Mediadores del Ministerio de Justicia.",
    },
  ];
  return (
    <section className="container-app py-16 border-t border-white/5">
      <div className="label-eyebrow">El Problema</div>
      <h2 className="serif text-4xl sm:text-5xl mt-3">Tres dolores estructurales<br/>que nadie había resuelto.</h2>
      <div className="mt-10 grid md:grid-cols-3 gap-4">
        {items.map((it, i) => (
          <div key={i} className="panel p-6" data-testid={`problem-${i}`}>
            <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center border border-white/10">{it.icon}</div>
            <h3 className="serif text-xl mt-4">{it.title}</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{it.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
function Solution() {
  return (
    <section className="container-app py-16 border-t border-white/5">
      <div className="label-eyebrow text-emerald-300">La Solución</div>
      <h2 className="serif text-4xl sm:text-5xl mt-3">
        Un ecosistema legal-tech<br/>con <span className="brand-gradient-text">dos módulos independientes</span>.
      </h2>
      <p className="mt-6 text-lg text-muted-foreground max-w-3xl">
        Un solo login, dos líneas de negocio operando en paralelo. Compartimos verificación, Pago Protegido y trust layer;
        cada módulo tiene su motor de precios y estados regulados propios.
      </p>
    </section>
  );
}

function ModulesGrid() {
  const { config } = useConfig();
  const bc = config?.business_config || {};
  const fee = Number(bc.cargo_fijo_mediacion ?? config?.colega_link_initial_fee_clp ?? 13200);
  const rate = Number(bc.porcentaje_comision_acuerdo ?? config?.colega_link_agreement_rate ?? 0.08);
  const cap = Number(bc.tope_total_solicitante_clp ?? config?.family_mediation_total_cap_clp ?? 130602);
  const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);
  return (
    <section className="container-app pb-10">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="panel p-8" data-testid="mod-delegaciones">
          <div className="pill badge-cyan text-xs"><Scale size={12}/> Módulo 1</div>
          <h3 className="serif text-3xl mt-4">Directorio de Delegaciones</h3>
          <p className="text-sm text-muted-foreground mt-2">Abogado ↔ Abogado</p>
          <ul className="mt-6 space-y-3 text-sm">
            <Feat>Publicación con tribunal, comuna, fecha/hora 24h, honorario y adjuntos</Feat>
            <Feat>Postulaciones de colegas verificados</Feat>
            <Feat><strong>Pago Protegido</strong> hasta cumplimiento</Feat>
            <Feat>Integración OJV (3 pasos con Clave Única pjud.cl)</Feat>
            <Feat>Editar publicación mientras esté abierta (con audit-log del diff)</Feat>
            <Feat>Rating y comentarios post-entrega</Feat>
          </ul>
        </div>

        <div className="panel p-8 border-emerald-400/25" data-testid="mod-mediacion">
          <div className="pill badge-emerald text-xs"><Layers size={12}/> Módulo 2</div>
          <h3 className="serif text-3xl mt-4">Mediación Familiar Privada</h3>
          <p className="text-sm text-muted-foreground mt-2">Mediador ↔ Persona solicitante</p>
          <ul className="mt-6 space-y-3 text-sm">
            <Feat>Directorio filtrable por Corte de Apelaciones (18) y comuna</Feat>
            <Feat><strong>Motor de precios único en Chile</strong>: {CLP(fee)} fijo + {Math.round(rate * 10000) / 100}% sobre saldo</Feat>
            <Feat>Tope legal máximo garantizado: <strong>{CLP(cap)}</strong></Feat>
            <Feat>15 estados regulados con audit trail</Feat>
            <Feat>Tarifario congelado al aceptar (inmutable)</Feat>
            <Feat>Suspensión automática 7d si no sube el acta en 24h</Feat>
          </ul>
        </div>
      </div>
    </section>
  );
}

function Feat({ children }) {
  return (
    <li className="flex items-start gap-2 text-muted-foreground">
      <Check size={15} className="text-emerald-300 shrink-0 mt-0.5"/>
      <span>{children}</span>
    </li>
  );
}

/* ─────────────────────────────────────────────────────────── */
function BusinessModel() {
  const { config } = useConfig();
  const bc = config?.business_config || {};
  const fee = Number(bc.cargo_fijo_mediacion ?? config?.colega_link_initial_fee_clp ?? 13200);
  const rate = Number(bc.porcentaje_comision_acuerdo ?? config?.colega_link_agreement_rate ?? 0.08);
  const cap = Number(bc.tope_total_solicitante_clp ?? config?.family_mediation_total_cap_clp ?? 130602);
  const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);
  return (
    <section className="container-app py-16 border-t border-white/5">
      <div className="label-eyebrow text-gold-300" style={{color: "#F5C542"}}>Modelo de negocio</div>
      <h2 className="serif text-4xl sm:text-5xl mt-3">5 líneas de ingreso.<br/>Todas activas hoy.</h2>

      <div className="mt-10 overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm" data-testid="business-model-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-white/10">
              <th className="py-3">Línea</th>
              <th className="py-3">Regla</th>
              <th className="py-3">Volumen mensual objetivo</th>
              <th className="py-3">Estado</th>
            </tr>
          </thead>
          <tbody>
            <Row line="Membresía Fundador anual" rule="$239.900 × usuario (cap 50)" vol="50 usuarios (locked)" active/>
            <Row line="Membresía Fundador mensual" rule="$25.990 × usuario × mes" vol="10-30 activos" active/>
            <Row line="Reserva Pionero" rule="$15.000 CLP no reembolsable · se abona al plan anual Fundador" vol="Primeros 50 abogados" active/>
            <Row line="Comisión delegaciones" rule="5% Founder / 12% Free (floor $30k)" vol="500-3.000 op/mes" active/>
            <Row line="Cargo fijo mediación" rule={`${CLP(fee)} CLP × mediación iniciada`} vol="60-400 op/mes" active/>
            <Row line="Comisión mediación acuerdo" rule={`${Math.round(rate * 10000) / 100}% sobre saldo (tope ${CLP(cap)})`} vol="30-260 op/mes" active/>
          </tbody>
        </table>
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-4" data-testid="business-highlights">
        <Highlight color="cyan" label="ARPU Founder efectivo" value="$19.992/mes" sub="Plan anual $239.900 con 2 meses gratis"/>
        <Highlight color="emerald" label="LTV/CAC Founder" value="~40x" sub="Payback < 1 mes en escenario Base"/>
        <Highlight color="gold" label="Margen EBITDA proyectado" value="60% → 77%" sub="Conservador → Optimista"/>
      </div>
    </section>
  );
}

function Row({ line, rule, vol, active }) {
  return (
    <tr className="border-b border-white/5">
      <td className="py-3 pr-4 font-medium">{line}</td>
      <td className="py-3 pr-4 text-muted-foreground">{rule}</td>
      <td className="py-3 pr-4 text-muted-foreground">{vol}</td>
      <td className="py-3">
        {active ? <span className="pill badge-verified text-[10px]"><Check size={9}/> Activo</span>
                : <span className="pill text-[10px]">Backlog</span>}
      </td>
    </tr>
  );
}

function Highlight({ color, label, value, sub }) {
  const map = {
    cyan: "text-cyan-300 border-cyan-400/25",
    emerald: "text-emerald-300 border-emerald-400/25",
    gold: "text-amber-300 border-amber-400/25",
  };
  return (
    <div className={`panel p-6 ${map[color]}`}>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="serif text-3xl mt-2">{value}</div>
      <div className="text-xs text-muted-foreground mt-2">{sub}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
function Prestaciones() {
  const cats = [
    {
      icon: <ShieldCheck size={18}/>,
      title: "Trust Layer",
      items: ["Verificación oficial Corte Suprema", "Registro Mediadores MinJusticia", "Rating post-servicio", "Auditoría inmutable en 4 collections"],
    },
    {
      icon: <Lock size={18}/>,
      title: "Pago Protegido",
      items: ["Pago Protegido administrativo", "Liberación por etapas OJV con Clave Única", "3 pasos pjud.cl", "Sistema de disputa"],
    },
    {
      icon: <TrendingUp size={18}/>,
      title: "Anti-fuga",
      items: ["Cargo mínimo $30.000 (sólo Free)", "Score de riesgo dinámico 0-100", "Panel /admin/riesgo", "Regex bloquea WhatsApp/emails/URLs"],
    },
    {
      icon: <Scale size={18}/>,
      title: "Mediación Familiar",
      items: ["Motor precios único en Chile", "Calculadora tiempo real con tope", "Suspensión auto 7d sin acta", "Rol Persona Solicitante"],
    },
    {
      icon: <Zap size={18}/>,
      title: "Notificaciones",
      items: ["Push + in-app", "Sonido celebración + vibración", "Broadcast regional", "Polling adaptativo 8-15s"],
    },
    {
      icon: <Rocket size={18}/>,
      title: "Growth & Referrals",
      items: ["Códigos AMIGO-NX-XXXX únicos", "50% OFF invitados", "Bonus delegación al Fundador", "Leaderboard mensual"],
    },
    {
      icon: <Users size={18}/>,
      title: "WhatsApp Marketing",
      items: ["Página /invitar con 4 plantillas", "wa.me sin costo", "Botón sharing viral en landing", "Grupo comunidad configurado"],
    },
    {
      icon: <MapPin size={18}/>,
      title: "Mobile & PWA",
      items: ["Instalable iOS/Android sin App Store", "Service Worker v2", "Push VAPID-ready", "Responsive mobile-first"],
    },
    {
      icon: <FileText size={18}/>,
      title: "Compliance",
      items: ["Términos versionados", "RUT módulo 11 estricto", "Horario 24h HH:MM", "Datos privados sólo a admin"],
    },
  ];
  return (
    <section className="container-app py-16 border-t border-white/5">
      <div className="label-eyebrow">Todo esto ya está construido</div>
      <h2 className="serif text-4xl sm:text-5xl mt-3">Nueve categorías de <br/>prestaciones en producción.</h2>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="prestaciones-grid">
        {cats.map((c, i) => (
          <div key={i} className="panel p-5" data-testid={`prest-${i}`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center text-cyan-300">{c.icon}</div>
              <h3 className="serif text-lg">{c.title}</h3>
            </div>
            <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
              {c.items.map((it, j) => (
                <li key={j} className="flex items-start gap-1.5">
                  <span className="text-emerald-300 mt-0.5">▸</span> {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
function Diferenciales() {
  const rows = [
    { compe: "Grupos de WhatsApp", debil: "Sin verificación · sin pago protegido", gan: "Verificación oficial + Pago Protegido administrativo" },
    { compe: "Directorios (Guía Legal)", debil: "Estáticos · sin transacción", gan: "Directorio transaccional integrado" },
    { compe: "Upwork · Workana", debil: "Genéricos · no entienden ley chilena", gan: "15 estados alineados a ley + OJV integrado" },
    { compe: "Software despacho (Themis)", debil: "Herramienta interna", gan: "Red bilateral colega ↔ colega" },
  ];
  return (
    <section className="container-app py-16 border-t border-white/5">
      <div className="label-eyebrow text-cyan-300">Diferenciales</div>
      <h2 className="serif text-4xl sm:text-5xl mt-3">Por qué somos<br/>el estándar emergente.</h2>
      <div className="mt-10 overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-white/10">
              <th className="py-3 pr-6">Competidor</th>
              <th className="py-3 pr-6">Debilidad</th>
              <th className="py-3">Cómo lo ganamos</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-white/5" data-testid={`diff-row-${i}`}>
                <td className="py-4 pr-6 font-medium">{r.compe}</td>
                <td className="py-4 pr-6 text-muted-foreground">{r.debil}</td>
                <td className="py-4 text-emerald-300">{r.gan}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
function Financials() {
  return (
    <section className="container-app py-16 border-t border-white/5">
      <div className="label-eyebrow">Proyección 24 meses</div>
      <h2 className="serif text-4xl sm:text-5xl mt-3">Tres escenarios modelados<br/>desde tarifas <span className="brand-gradient-text">reales</span>.</h2>
      <p className="mt-4 text-muted-foreground max-w-3xl">
        Todos los cálculos parten de los precios actualmente cargados en producción (PLANS, env vars) y usan benchmarks de conversión de SaaS legal-tech LATAM.
      </p>

      <div className="mt-10 grid md:grid-cols-3 gap-4" data-testid="fin-scenarios">
        <ScenarioCard tone="muted" name="Conservador"
          ingresos="$54,5 M" ebitda="$32,5 M" margin="60%"
          notes={["Sin campaña grande", "Founder cap cerrado mes 18", "Free 1.200 en 24m"]}/>
        <ScenarioCard tone="cyan" name="Base" featured
          ingresos="$312,3 M" ebitda="$217,3 M" margin="70%"
          notes={["Marketing profesional activo", "Founder cap cerrado mes 5", "Free 3.000 en 24m"]}/>
        <ScenarioCard tone="emerald" name="Optimista"
          ingresos="$1.808,5 M" ebitda="$1.388,5 M" margin="77%"
          notes={["Viralidad + expansión LATAM", "Founder cap cerrado mes 2", "Free 15.000 en 24m (CL+PE+CO)"]}/>
      </div>

      <div className="mt-8 panel p-6" data-testid="tam-panel">
        <h3 className="serif text-xl">Mercado direccionable</h3>
        <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">TAM Chile</div>
            <div className="serif text-2xl text-cyan-300 mt-1">US$ 44 M</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">SAM LATAM hispano</div>
            <div className="serif text-2xl text-emerald-300 mt-1">US$ 400 M+</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">SOM realista año 2</div>
            <div className="serif text-2xl text-amber-300 mt-1">US$ 4-6 M</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ScenarioCard({ tone, name, ingresos, ebitda, margin, notes, featured }) {
  const map = {
    cyan: "border-cyan-400/40 bg-cyan-400/5",
    emerald: "border-emerald-400/40 bg-emerald-400/5",
    muted: "border-white/10",
  };
  return (
    <div className={`panel p-6 ${map[tone]} ${featured ? "ring-1 ring-cyan-400/40" : ""}`} data-testid={`scenario-${name.toLowerCase()}`}>
      {featured && <div className="pill badge-gold text-[10px] mb-3">MÁS PROBABLE</div>}
      <h3 className="serif text-2xl">{name}</h3>
      <div className="mt-4 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-muted-foreground">Ingresos 24m</span><span className="font-medium">{ingresos}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">EBITDA</span><span className="font-medium">{ebitda}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Margen</span><span className="font-medium text-emerald-300">{margin}</span></div>
      </div>
      <ul className="mt-4 pt-4 border-t border-white/5 space-y-1 text-xs text-muted-foreground">
        {notes.map((n, i) => <li key={i}>▸ {n}</li>)}
      </ul>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
function Assets() {
  const cards = [
    { icon: <Video size={18}/>, title: "Video Pitch Inversionistas 90s", desc: "Recién producido: 7 escenas narradas con voz TTS + música de branding. Formato 1920×1080.", href: `${BACKEND}/api/investor-video`, cta: "Ver ahora" },
    { icon: <FileText size={18}/>, title: "Brief PDF profesional", desc: "8 páginas con branding oficial, tablas de proyección y ask concreto.", href: `${BACKEND}/api/investor-brief.pdf`, cta: "Descargar" },
    { icon: <Globe size={18}/>, title: "Landing /embajador-pionero", desc: "Página viral con counter Fundador X/50, 6 BenefitCards y CTAs anti-objeción.", href: "/embajador-pionero", cta: "Ver landing" },
    { icon: <FileText size={18}/>, title: "Guía de inducción", desc: "15 secciones interactivas cubriendo todo el ciclo de uso.", href: "/guia", cta: "Ver guía" },
    { icon: <Users size={18}/>, title: "Comunidad WhatsApp", desc: "Grupo activo con abogados y mediadores. Únete y conoce a la comunidad Colega Link en tiempo real.", href: "https://chat.whatsapp.com/HDlP1R3qmgz60kLfTImt8T", cta: "Unirme al grupo" },
    { icon: <Play size={18}/>, title: "Producto en vivo", desc: "Explora la aplicación completa en producción con las credenciales demo.", href: "https://www.colegalink.cl", cta: "Ir al producto" },
  ];
  return (
    <section className="container-app py-16 border-t border-white/5">
      <div className="label-eyebrow text-amber-300">Assets listos</div>
      <h2 className="serif text-4xl sm:text-5xl mt-3">Todo lo creativo<br/>ya está producido.</h2>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assets-grid">
        {cards.map((c, i) => (
          <a key={i} href={c.href} target={c.href.startsWith("http") || c.href.startsWith("/api") ? "_blank" : "_self"}
             rel="noopener noreferrer"
             onClick={() => trackCTA(`asset_${c.title.toLowerCase().replace(/\s+/g,'_')}`)}
             className="panel p-5 hover:bg-white/[0.02] transition-colors group" data-testid={`asset-${i}`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-400/10 border border-amber-400/25 flex items-center justify-center text-amber-300">{c.icon}</div>
              <h3 className="serif text-lg">{c.title}</h3>
            </div>
            <p className="mt-3 text-xs text-muted-foreground leading-relaxed">{c.desc}</p>
            <div className="mt-4 text-xs text-cyan-300 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
              {c.cta} <ArrowRight size={12}/>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
function Ask() {
  const options = [
    "Equity puro (participación accionaria por aporte de expertise)",
    "Revenue share sobre líneas de negocio específicas",
    "Combinación equity + retainer mensual + performance bonus",
    "Adquisición completa (venta de la empresa post-diligencia)",
  ];
  const useOfFunds = [
    { pct: "45%", cat: "Marketing & adquisición", detail: "Ads Meta/TikTok/LinkedIn + producción de contenido" },
    { pct: "25%", cat: "Producto & engineering", detail: "Pasarela real + FES + almacenamiento S3 + refactor" },
    { pct: "20%", cat: "Equipo comercial", detail: "1 BDR full-time + community manager part-time" },
    { pct: "7%", cat: "Legal & compliance", detail: "Términos actualizados + protección datos + cuenta de Pago Protegido" },
    { pct: "3%", cat: "Contingencia", detail: "Buffer operativo" },
  ];
  return (
    <section className="container-app py-16 border-t border-white/5">
      <div className="label-eyebrow text-emerald-300">La Oportunidad</div>
      <h2 className="serif text-4xl sm:text-5xl mt-3">Lo que ofrezco.<br/>Lo que necesito.</h2>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <div className="panel p-6" data-testid="ask-modalidades">
          <h3 className="serif text-xl">Modalidades de partnership abiertas</h3>
          <ul className="mt-5 space-y-3 text-sm">
            {options.map((o, i) => (
              <li key={i} className="flex items-start gap-2 text-muted-foreground">
                <Check size={14} className="text-emerald-300 shrink-0 mt-0.5"/> {o}
              </li>
            ))}
          </ul>
        </div>

        <div className="panel p-6" data-testid="ask-funds">
          <h3 className="serif text-xl">Uso de capital sugerido</h3>
          <p className="text-xs text-muted-foreground mt-1">Si hay ronda simultánea de US$ 150.000</p>
          <ul className="mt-5 space-y-3 text-sm">
            {useOfFunds.map((u, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="serif text-lg text-cyan-300 min-w-[50px]">{u.pct}</span>
                <div>
                  <div className="font-medium">{u.cat}</div>
                  <div className="text-xs text-muted-foreground">{u.detail}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-5 pt-4 border-t border-white/5 text-xs text-emerald-300">
            Runway estimado con capital + revenue Base: ~18 meses.
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
function CTAFinal() {
  return (
    <section className="container-app py-20 border-t border-white/5">
      <div className="panel p-10 lg:p-14 text-center" data-testid="cta-final-panel">
        <div className="label-eyebrow text-cyan-300">Próximo paso</div>
        <h2 className="serif text-4xl sm:text-6xl mt-4 max-w-4xl mx-auto leading-tight">
          Una reunión de 45 minutos.<br/>
          Demo en vivo, panel admin, y hablamos del deal.
        </h2>
        <p className="mt-6 text-muted-foreground max-w-2xl mx-auto">
          La plataforma se puede probar ya mismo en <a href="https://www.colegalink.cl" className="text-cyan-300 hover:underline">www.colegalink.cl</a>.
          Credenciales admin bajo NDA para ver el motor por dentro.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <a href="https://wa.me/56941199600?text=Hola%20Cristian%2C%20quiero%20agendar%20la%20demo%20de%20Colega%20Link"
             target="_blank" rel="noopener noreferrer"
             onClick={() => trackCTA("final_whatsapp")}
             className="btn-primary" data-testid="cta-final-whatsapp">
            Agendar por WhatsApp <ArrowRight size={14} className="ml-1"/>
          </a>
          <a href={`${BACKEND}/api/investor-brief.pdf`} target="_blank" rel="noopener noreferrer"
             onClick={() => trackCTA("final_pdf")}
             className="btn-ghost" data-testid="cta-final-pdf">
            <Download size={14} className="mr-1.5"/> Descargar Brief PDF
          </a>
          <Link to="/" className="btn-ghost" data-testid="cta-final-home">
            Ir al sitio principal
          </Link>
        </div>
        <div className="mt-10 text-xs text-muted-foreground">
          Colega Link · Investor Brief · Julio 2026 · Documento generado desde el codebase real y auditable
        </div>
      </div>
    </section>
  );
}
